import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import { stockInterrogatingList } from 'modules/common/forms/materialWidget/data/actions';
import { cadViewer } from 'modules/common/viewer/cadViewer/data/actions';
import { partFileStatuses } from 'modules/viewerPage/data/actions';
import {
  getActiveFileUUID,
  getActivePartFile,
  getTechnicalDataPackage,
} from 'modules/viewerPage/data/reducer';

import partLibraryService from 'services/partLibraryService';

import { types, geometricInterrogation } from './actions';
import { getQuery, getSelectedResult } from './reducer';

export default function* root() {
  yield takeLatest(types.CLEAR, watchClear);
  yield takeLatest(types.RECEIVE_QUERY, watchReceiveQuery);
  yield takeLatest(
    types.SET_WITH_INTERROGATION_FILE,
    watchSetWithInterrogationFile,
  );
  yield takeLatest(types.SUBMIT, watchSubmit);
}

function* watchClear() {
  yield all([
    put(cadViewer.receiveInterrogationInfo({})),
    put(cadViewer.setGeometricEntities({})),
  ]);
}

function* watchReceiveQuery(action) {
  const { material, task, shouldInit, useSelectedTask } = action.payload;

  let useTask = task;
  if (useSelectedTask) {
    const selectedResult = yield select(getSelectedResult);
    useTask = (selectedResult || {}).task || task;
  }

  try {
    const params = { task: useTask };
    if (material) {
      if (material[0]) params.materialClassId = material[0];
      if (material[1]) params.materialFamilyId = material[1];
      if (material[2]) params.materialId = material[2];
    }
    const uuid = yield select(getActiveFileUUID);
    const results = yield call(partLibraryService.listCustomInterrogations, {
      uuid,
      params,
    });
    yield put(geometricInterrogation.receiveSearchResults({ results }));

    if (shouldInit) {
      yield call(collectDefaultState, results);
    } else {
      yield call(collectLatentState, results);
    }
  } catch (error) {
    console.error(error);
  }
}

function* collectDefaultState(results) {
  const nonDefaultResults = results.filter((r) => r.id);
  const defaultResults = results.filter((r) => !r.id);

  let result;
  for (const entry of nonDefaultResults.concat(defaultResults)) {
    if (entry.interrogationFileId) {
      result = entry;
      break;
    }
  }

  const { documentHash, interrogationFileId, task } = result || {};
  yield all([
    put(geometricInterrogation.receiveResultForDefault({ result })),
    put(
      cadViewer.receiveInterrogationInfo({
        documentHash,
        interrogationFileId,
        task,
      }),
    ),
    put(cadViewer.setGeometricEntities({})),
  ]);
}

function* collectLatentState(results) {
  // compare results against stored options in the result
  const selectedResult = yield select(getSelectedResult);
  if (!selectedResult) return;

  const nonDefaultResults = results.filter((r) => r.id);
  const defaultResults = results.filter((r) => !r.id);

  let result;
  for (const entry of nonDefaultResults.concat(defaultResults)) {
    if (entry.documentHash === selectedResult.documentHash) {
      result = entry;
      break;
    }
  }

  const { documentHash, interrogationFileId, task } = result || {};

  yield all([
    put(geometricInterrogation.receiveResultForDefault({ result })),
    put(
      cadViewer.receiveInterrogationInfo({
        documentHash,
        interrogationFileId,
        task,
      }),
    ),
    put(cadViewer.setGeometricEntities({})),
  ]);
}

function* watchSetWithInterrogationFile(action) {
  const { payload } = action;
  const { partFileUuid, partInterrogationFile, showAdditionalKeysExplicit } =
    payload;

  const { id: interrogationFileId, documentHash, task } = partInterrogationFile;

  // try to collect the result form a custom interrogation. if none exist, show a fake
  // result with features without an associated interrogation file
  const params = { task };
  const results = yield call(partLibraryService.listCustomInterrogations, {
    uuid: partFileUuid,
    params,
  });
  yield put(geometricInterrogation.receiveSearchResults({ results }));

  const nonDefaultResults = results.filter((r) => r.id);
  const defaultResults = results.filter((r) => !r.id);

  let result;
  for (const entry of nonDefaultResults.concat(defaultResults)) {
    if (entry.interrogationFileId == interrogationFileId) {
      result = entry;
      break;
    }
  }

  if (!result) {
    result = {
      task,
      documentHash,
      interrogationFileId,
      name: task,
    };
  }

  yield all([
    put(geometricInterrogation.receiveResultForDefault({ result })),
    put(
      cadViewer.receiveInterrogationInfo({
        documentHash: result.documentHash,
        interrogationFileId: result.interrogationFileId,
        task: result.task,
      }),
    ),
    put(
      cadViewer.setGeometricEntities({
        showAdditionalKeysExplicit,
      }),
    ),
  ]);
}

function* watchSubmit(action) {
  try {
    const { result } = action.payload;
    const {
      documentHash,
      id: customInterrogationId,
      interrogationFileId,
      task,
    } = result;

    yield put(
      cadViewer.receiveInterrogationInfo({
        documentHash,
        interrogationFileId,
        task,
      }),
    );

    const activePartFile = yield select(getActivePartFile);
    const { forbiddenInterrogationInformation, isDerivedFromInterrogation } =
      activePartFile;
    const forbiddenMessage = (
      forbiddenInterrogationInformation.find((info) => info.task === task) || {}
    ).forbiddenMessage;

    if (forbiddenMessage || isDerivedFromInterrogation) {
      yield put(cadViewer.setGeometricEntities({}));
    } else if (interrogationFileId) {
      // check for if the part interrogation file is in data package, else dispatch interrogation
      // request flow to fetch results through surrounding tech data package sagas
      const technicalDataPackage = yield select(getTechnicalDataPackage);
      const { partInterrogationFiles } = technicalDataPackage;
      const foundInterrogationFile = partInterrogationFiles.find(
        (pif) =>
          pif.partFileUuid === activePartFile.uuid &&
          pif.documentHash === documentHash,
      );
      if (foundInterrogationFile) {
        yield put(cadViewer.setGeometricEntities({}));
      } else {
        yield call(interrogationRequest, {
          activePartFile,
          customInterrogationId,
          task,
        });
      }
    } else {
      yield call(interrogationRequest, {
        activePartFile,
        customInterrogationId,
        task,
      });
    }
  } catch (error) {
    console.error('There was an error during interrogation', error);
  }
}

function* interrogationRequest({
  activePartFile,
  customInterrogationId,
  task,
}) {
  // interrogation request
  try {
    const query = yield select(getQuery);
    const { material } = query;
    const materialId = material && material.length == 3 ? material[2] : null;
    const response = yield call(partLibraryService.interrogationRequest, {
      uuid: activePartFile.uuid,
      data: {
        customInterrogationId,
        materialId,
        task,
      },
    });
    const nextInterrogationStatuses = {
      [activePartFile.uuid]: response,
    };
    yield put(
      partFileStatuses.receiveInterrogationStatuses({
        interrogationStatuses: nextInterrogationStatuses,
      }),
    );
    yield put(stockInterrogatingList.add({ uuid: activePartFile.uuid }));
  } catch (error) {
    console.error(error);
  }
}
